import { createAction } from "@reduxjs/toolkit";
import { typeFullSalesItem } from "model/dashboardSales/dSalesReducer";
import { IdStoresStateDataItem } from "model/dashboardStores/dStoresReducer";
import { typePagination, typeRequestPagination } from "model/system/constants";
import {
  typeNewOrderRequest,
  typeEditedOrderRequest,
} from "model/dashboardOrders/dOrdersAPI";
import {
  typeOrdersFilters,
  typeOrdersItemFull,
  typeOrdersItemSlim,
} from "model/dashboardOrders/dOrdersReducer";
import {
  typeProductsFilters,
  IdProductsStateDataItem,
} from "model/dashboardProducts/dProductsReducer";

// ---------------------------------------------------------------------------------------------------------------------
// Action creator of set pagination
export const dOrdersSetPaginationAC = createAction(
  "D_ORDERS_SET_PAGINATION",
  (pagination: typePagination) => ({ payload: { pagination } })
);

export const dOrdersProductsSetPaginationAC = createAction(
  "D_ORDERS_PRODUCTS_SET_PAGINATION",
  (pagination: typePagination) => ({ payload: { pagination } })
);

// Action creator of set itemsForPage in pagination
export const dOrdersSetItemsForPageAC = createAction(
  "D_ORDERS_SET_ITEMS_FOR_PAGE",
  (itemsForPage: number) => ({ payload: { itemsForPage } })
);

// Action creator of set pageNumber in pagination
export const dOrdersSetPageNumberAC = createAction(
  "D_ORDERS_SET_PAGE_NUMBER",
  (pageNumber: number) => ({ payload: { pageNumber } })
);

// ---------------------------------------------------------------------------------------------------------------------

// Action creator of set data
export const dOrdersSetDataAC = createAction(
  "D_ORDERS_SET_DATA",
  (dataList: typeOrdersItemSlim[]) => ({ payload: { dataList } })
);

// Action creator of saga set data
export const dOrdersSetDataSagaAC = createAction(
  "D_ORDERS_SET_DATA_SAGA",
  (filters: typeOrdersFilters, paginationRequest: typeRequestPagination) => ({
    payload: { filters, paginationRequest },
  })
);

// ---------------------------------------------------------------------------------------------------------------------
// Action creator of set data detail
export const dOrdersSetDetailItemAC = createAction(
  "D_ORDERS_SET_DETAIL_ITEM",
  (detailItemId: string) => ({ payload: detailItemId })
);

// Action creator of set data detail Full
export const dOrdersSetDetailItemFullAC = createAction(
  "D_ORDERS_SET_DETAIL_ITEM_FULL",
  (detailItemFull: typeOrdersItemFull | undefined) => ({
    payload: detailItemFull,
  })
);

// Action creator of clear detail data
export const dOrdersClearDetailItemAC = createAction(
  "D_ORDERS_CLEAR_DETAIL_ITEM"
);
export const dOrdersSetDetailItemFullSagaAC = createAction(
  "D_ORDERS_SET_DETAIL_ITEM_FULL_SAGA",
  (orderId: string) => ({ payload: { orderId } })
);

// Action creator of saga add item
export const dOrdersAddItemSagaAC = createAction(
  "D_ORDERS_ADD_SAGA_ITEM",
  (
    itemObject: typeNewOrderRequest,
    filters: typeOrdersFilters,
    paginationRequest: typeRequestPagination
  ) => ({ payload: { itemObject, filters, paginationRequest } })
);

// ---------------------------------------------------------------------------------------------------------------------

// Action creator of saga edit data
export const dOrdersEditItemSagaAC = createAction(
  "D_ORDERS_EDIT_SAGA_ITEM",
  (
    detailItemObject: typeEditedOrderRequest,
    filters: typeOrdersFilters,
    paginationRequest: typeRequestPagination
  ) => ({ payload: { detailItemObject, filters, paginationRequest } })
);

// ---------------------------------------------------------------------------------------------------------------------
// Action creator of clear filtersForm
export const dOrdersClearFiltersAC = createAction("D_ORDERS_CLEAR_FILTERS");

// Action creator of set filtersForm
export const dOrdersSetFiltersAC = createAction(
  "D_ORDERS_SET_FILTERS",
  (filterObj: typeOrdersFilters) => ({ payload: filterObj })
);

// ---------------------------------------------------------------------------------------------------------------------
// Action creator of saga set data
export const dOrdersSetFilteredProductsAC = createAction(
  "D_ORDERS_SET_FILTERED_PRODUCTS",
  (data: IdProductsStateDataItem[]) => ({ payload: { data } })
);
// ---------------------------------------------------------------------------------------------------------------------
// Action creator of set checked item
export const dOrdersCheckedItemAC = createAction(
  "D_ORDERS_CHECK_ITEM",
  (itemId: string) => ({ payload: itemId })
);

// Action creator of checked all items
export const dOrdersCheckedAllItemAC = createAction(
  "D_ORDERS_CHECK_ALL_ITEM",
  (value: boolean) => ({ payload: value })
);

// ---------------------------------------------------------------------------------------------------------------------
// Action creator of clear product filters
export const dOrdersClearProductFiltersAC = createAction(
  "D_ORDERS_CLEAR_PRODUCT_FILTERS"
);

// Action creator of set product filters
export const dOrdersSetProductFiltersAC = createAction(
  "D_ORDERS_SET_PRODUCT_FILTERS",
  (filterObj: typeProductsFilters) => ({ payload: filterObj })
);
// ---------------------------------------------------------------------------------------------------------------------
// Action creator of saga set data
export const dOrdersSetFilteredProductsSagaAC = createAction(
  "D_ORDERS_SET_FILTERED_PRODUCTS_SAGA",
  (filters: typeProductsFilters, paginationRequest: typeRequestPagination) => ({
    payload: { filters, paginationRequest },
  })
);
// ---------------------------------------------------------------------------------------------------------------------
// Action creator of set selected store
export const dOrdersSetSelectedStoreAC = createAction(
  "D_ORDERS_SET_SELECTED_STORE",
  (store: IdStoresStateDataItem | undefined) => ({ payload: { store } })
);
// ---------------------------------------------------------------------------------------------------------------------
// Action creator of set detail product
export const dOrdersSetDetailProductAC = createAction(
  "D_ORDERS_SET_DETAIL_PRODUCT",
  (product: IdProductsStateDataItem | string | undefined) => ({
    payload: product,
  })
);

// ---------------------------------------------------------------------------------------------------------------------

// Action creator of add product to cart
export const dOrdersAddProductToCartAC = createAction(
  "D_ORDERS_ADD_PRODUCT_TO_CART",
  (
    product: IdProductsStateDataItem,
    price: number,
    markLabel?: string,
    barcode?: string
  ) => ({ payload: { product, price, markLabel, barcode } })
);

// ---------------------------------------------------------------------------------------------------------------------
// Action creator of delete product from cart
export const dOrdersDeleteProductFromCartItemAC = createAction(
  "D_ORDERS_DELETE_PRODUCT_FROM_CART_ITEM",
  (productIndex: number) => ({ payload: { productIndex } })
);

// ---------------------------------------------------------------------------------------------------------------------
// Action creator of set count of product cart
export const dOrdersSetCountForProductInCartItemAC = createAction(
  "D_ORDERS_SET_COUNT_OF_PRODUCT_IN_CART_ITEM",
  (id: string, count: number) => ({ payload: { id, count } })
);

// ---------------------------------------------------------------------------------------------------------------------
// Action creator of set price of product in cart
export const dOrdersSetPriceForProductInCartItemAC = createAction(
  "D_ORDERS_SET_PRICE_OF_PRODUCT_IN_CART_ITEM",
  (id: string, price: string) => ({ payload: { id, price } })
);

// ---------------------------------------------------------------------------------------------------------------------
// Action creator of clear cart
export const dOrdersClearCartItemAC = createAction("D_ORDERS_CLEAR_CART_ITEM");
// ---------------------------------------------------------------------------------------------------------------------
// Action creator of clear cart
export const dOrdersSetCartItemAC = createAction(
  "D_ORDERS_SET_CART_ITEM",
  (data: typeOrdersItemFull) => ({ payload: data })
);

// ---------------------------------------------------------------------------------------------------------------------
// Action creator of set discount cart
export const dOrdersSetDiscountInCartItemAC = createAction(
  "D_ORDERS_SET_DISCOUNT_IN_CART_ITEM",
  (isPercent: boolean, value: number) => ({ payload: { isPercent, value } })
);
// ---------------------------------------------------------------------------------------------------------------------
// Action creator of set service payment cart
export const dOrdersSetServicePaymentInCartItemAC = createAction(
  "D_ORDERS_SET_SERVICE_PAYMENT_IN_CART_ITEM",
  (isPercent: boolean, value: number) => ({ payload: { isPercent, value } })
);

// ---------------------------------------------------------------------------------------------------------------------
// Action creator of set mark label of product in cart
export const dOrdersSetMarkLabelForProductInCartItemAC = createAction(
  "D_ORDERS_SET_MARK_LABEL_OF_PRODUCT_IN_CART_ITEM",
  (index: number, marklabel: string) => ({ payload: { index, marklabel } })
);

// ---------------------------------------------------------------------------------------------------------------------
// Action creator of set sale detail
export const dOrdersSetSaleDetailItemSagaAC = createAction(
  "D_ORDERS_SET_SALE_DETAIL_ITEM_SAGA",
  (saleId: string) => ({ payload: { saleId } })
);
// Action creator of set sale detail
export const dOrdersSetSaleDetailItemC = createAction(
  "D_ORDERS_SET_SALE_DETAIL_ITEM",
  (saleItem: typeFullSalesItem | undefined) => ({ payload: saleItem })
);

// ---------------------------------------------------------------------------------------------------------------------
// Action creator of bulk change status
export const dOrdersChangeStatusBulkSagaAC = createAction(
  "D_ORDERS_CHANGE_STATUS_BULK_SAGA",
  (
    data: { id: string; status: string }[],
    filters: typeOrdersFilters,
    paginationRequest: typeRequestPagination
  ) => ({ payload: { data, filters, paginationRequest } })
);

export const dOrdersExportDataSagaAC = createAction(
  "D_ORDERS_EXPORT_DATA_SAGA",
  (filters: typeOrdersFilters, lang: string) => ({
    payload: { filters, lang },
  })
);

export const dOrdersSelectedExportDataSagaAC = createAction(
  "D_ORDERS_SELECTED_EXPORT_DATA_SAGA",
  (salesIds: string[], lang: string) => ({ payload: { salesIds, lang } })
);
